import React, { useEffect, useRef } from "react";

// HLS (.m3u8) playback for the live-blackjack video layer.
// Native HLS where the browser supports it (Safari/iOS); hls.js elsewhere
// (Chrome/Edge/Firefox), dynamically imported so it never blocks initial load.
// Used both as a legacy provider and as the WebRTC fallback.

export default function HlsPlayer({ src, muted = true, onHealth, onExhausted, className = "", "data-testid": testId }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const aliveRef = useRef(true);
  const onHealthRef = useRef(onHealth);
  const onExhaustedRef = useRef(onExhausted);
  onHealthRef.current = onHealth;
  onExhaustedRef.current = onExhausted;

  useEffect(() => {
    aliveRef.current = true;
    const video = videoRef.current;
    const health = (s) => { if (aliveRef.current && onHealthRef.current) onHealthRef.current(s); };
    if (!video || !src) { health("offline"); return undefined; }

    health("connecting");
    let cancelled = false;

    const nativeHls = video.canPlayType("application/vnd.apple.mpegurl");
    if (nativeHls) {
      video.src = src;
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      // Attempt hls.js; degrade gracefully if it isn't installed.
      import("hls.js")
        .then((mod) => {
          if (cancelled || !aliveRef.current) return;
          const Hls = mod.default || mod;
          if (Hls && Hls.isSupported && Hls.isSupported()) {
            const hls = new Hls({ lowLatencyMode: true, backBufferLength: 30 });
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.ERROR, (_e, data) => {
              if (data && data.fatal) { health("reconnecting"); try { hls.recoverMediaError(); } catch { /* noop */ } }
            });
          } else {
            video.src = src; // last-resort native attempt
          }
        })
        .catch(() => { if (!cancelled) { video.src = src; } });
    }

    const onPlaying = () => health("live");
    const onWaiting = () => health("reconnecting");
    const onError = () => { health("offline"); if (typeof onExhaustedRef.current === "function") onExhaustedRef.current(); };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("error", onError);

    return () => {
      cancelled = true;
      aliveRef.current = false;
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("error", onError);
      if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* noop */ } hlsRef.current = null; }
      try { video.removeAttribute("src"); video.load(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted={muted}
      playsInline
      controls={false}
      className={`absolute inset-0 h-full w-full object-cover ${className}`}
      data-testid={testId || "bjv2-hls"}
    />
  );
}
